import React, { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
  PreviewCard,
} from "../../components/Component";
import { RegistrationForm } from "./RegistrationForm";
import tenderApis from "../../api/tender/tender";
import Swal from "sweetalert2";

export const formatRegistrationDataForApi = (formData: any) => {
  const supplierData: any = {
    bussinessType: formData.bussinessType || "",
    gstin: formData.gstin || "",
    pan: formData.pan || "",
    supplierName: formData.supplierName || "",
    tradeName: formData.tradeName || "",
    brandName: formData.brandName || "",
    deliveryLeadDays: formData.deliveryLeadDays || "",
    contactPerson: formData.contactPerson || "",
    phoneNumber: formData.phoneNumber || "",
    email: formData.email || "",
    country: formData.country || "",
    state: formData.state || "",
    district: formData.district || "",
    city: formData.city || "",
    area: formData.area || "",
    pincode: formData.pincode || "",
    addressLine1: formData.addressLine1 || "",
    addressLine2: formData.addressLine2 || "",
    additionalContacts: [],
    materials: [],
    categories: [],
    supplyStates: [],
    applicationAccess: "No",
    paymentTerms: "",
    referenceID: "",
    lastFYTurnover: 0,
    incorporationDate: "",
    cin: "",
    companyName: "",
    mailingAddressLine1: "",
    accountHolderName: "",
    accountNumber: "",
    bankName: "",
    branch: "",
    ifscCode: "",
  };

  return {
    files: [],
    filesData: {
      newfiledata: [],
      deletedFileData: [],
      updatedFileData: [],
    },
    supplierData,
  };
};

interface SupplierRegistrationProps {
  onComplete: (data: any) => void;
  onBack?: () => void;
  initialEmailOrPhone?: string;
  initialName?: string;
  buyerOrgId?: number | null;
}

const SupplierRegistration: React.FC<SupplierRegistrationProps> = ({
  onComplete,
  onBack,
  initialEmailOrPhone,
  initialName,
  buyerOrgId,
}) => {
  const formMethods = useForm({
    mode: "onChange",
    defaultValues: {
      email: initialEmailOrPhone?.includes("@") ? initialEmailOrPhone : "",
      phoneNumber: !initialEmailOrPhone?.includes("@")
        ? initialEmailOrPhone
        : "",
      contactPerson: initialName || "",
    },
  });

  useEffect(() => {
    if (initialEmailOrPhone) {
      if (initialEmailOrPhone.includes("@")) {
        formMethods.setValue("email", initialEmailOrPhone);
      } else {
        formMethods.setValue("phoneNumber", initialEmailOrPhone);
      }
    }
    if (initialName) {
      formMethods.setValue("contactPerson", initialName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEmailOrPhone, initialName]);

  const handleSubmit = async (formData?: any) => {
    let response: any = null;
    try {
      const allFormData: any = formData || formMethods.getValues();

      const supplierFormData = new FormData();

      const supplierData: any = {
        bussinessType: allFormData.bussinessType || "",
        gstin: allFormData.gstin || "",
        pan: allFormData.pan || "",
        supplierName: allFormData.supplierName || "",
        tradeName: allFormData.tradeName || "",
        brandName: allFormData.brandName || "",
        deliveryLeadDays: allFormData.deliveryLeadDays || "",
        contactPerson: allFormData.contactPerson || "",
        phoneNumber: allFormData.phoneNumber || "",
        email: allFormData.email || "",
        country: allFormData.country || "",
        state: allFormData.state || "",
        district: allFormData.district || "",
        city: allFormData.city || "",
        area: allFormData.area || "",
        pincode: allFormData.pincode || "",
        addressLine1: allFormData.addressLine1 || "",
        addressLine2: allFormData.addressLine2 || "",
        additionalContacts: [],
        materials: [],
        categories: [],
        supplyStates: [],
        applicationAccess: "No",
        paymentTerms: "",
        referenceID: "",
        lastFYTurnover: 0,
        incorporationDate: "",
        cin: "",
        companyName: "",
        mailingAddressLine1: "",
        accountHolderName: "",
        accountNumber: "",
        bankName: "",
        branch: "",
        ifscCode: "",
        buyerOrgId: buyerOrgId,
      };

      supplierFormData.append("supplierData", JSON.stringify(supplierData));

      const filesData: any[] = [];
      supplierFormData.append("filesData", JSON.stringify(filesData));

      response = await tenderApis.createPublicSupplier(supplierFormData);

      if (response instanceof Error) {
        throw response;
      }

      if (response?.status) {
        onComplete({
          ...allFormData,
          apiResponse: response,
        });
      } else {
        const errorMessage =
          response?.message || "Failed to create supplier. Please try again.";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMessage,
          confirmButtonText: "OK",
          confirmButtonColor: "#3085d6",
        });
      }
    } catch (error: any) {
      console.error("Error creating supplier:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        response?.message ||
        "Failed to create supplier. Please try again.";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
      });
    }
  };

  return (
    <>
      <BlockHead size="lg">
        <BlockHeadContent>
          <BlockTitle tag="h4">Supplier Registration</BlockTitle>
          <BlockDes>
            Please complete the registration form to proceed with the tender.
          </BlockDes>
        </BlockHeadContent>
      </BlockHead>
      <div style={{ position: "relative", zIndex: 10, pointerEvents: "auto" }}>
        <Block>
          <PreviewCard
            className={"cardBox"}
            bodyClass="card-inner-xl"
            style={{ position: "relative", zIndex: 10, pointerEvents: "auto" }}
          >
            <div
              style={{
                position: "relative",
                zIndex: 10,
                pointerEvents: "auto",
              }}
            >
              <FormProvider {...formMethods}>
                <RegistrationForm onSubmit={handleSubmit} onBack={onBack} />
              </FormProvider>
            </div>
          </PreviewCard>
        </Block>
      </div>
    </>
  );
};

export default SupplierRegistration;
